import type { DeviceModelId } from "@/api/model/device";
import type { AUTProject, AUTProperty, AUTReservoir, AUTSector, AUTWaterPump } from "@/api/queries/account";
import { isLICDeviceModel } from "./device-model";

/**
 * Device association information
 */
export interface DeviceAssociation {
  type: "project" | "water_pump" | "reservoir" | "sector";
  name: string;
  id: string;
  description?: string;
}

/**
 * Sector-project association for valve controllers
 */
export interface SectorProjectAssociation {
  sectorName: string;
  sectorId: string;
  projectName: string;
  projectId: string;
}

/**
 * Find all associations for a device based on its ID and model
 * @param deviceId - The device UUID to find associations for
 * @param deviceModel - The device model type
 * @param property - The property containing all related data
 * @returns Array of associations for the device
 */
export function findDeviceAssociations(
  deviceId: string,
  deviceModel: DeviceModelId,
  property: AUTProperty | null
): DeviceAssociation[] {
  if (!property) return [];

  const associations: DeviceAssociation[] = [];

  const propDevice = property.devices.find((pd) => pd.device.id === deviceId);
  if (!propDevice) return [];

  if (isLICDeviceModel(propDevice.device.model)) {
    const licProjects = property.projects.filter((project) => project.localized_irrigation_controller === deviceId);
    associations.push(
      ...licProjects.map((project) => ({
        type: "project" as const,
        name: project.name,
        id: project.id,
        description: project.description || undefined,
      }))
    );
    return associations;
  }

  return propDevice.device.virtual_device_configurations
    .map((vdc) => {
      switch (vdc.equipment_type) {
        case "pump": {
          const waterPump = property.water_pumps.find((pump) => pump.id === vdc.water_pump);
          if (!waterPump) return null;
          return {
            type: "water_pump" as const,
            name: waterPump.label,
            id: waterPump.id,
          };
        }
        case "reservoir": {
          const reservoir = property.reservoirs.find((res) => res.id === vdc.reservoir);
          if (!reservoir) return null;
          return {
            type: "reservoir" as const,
            name: reservoir.name,
            id: reservoir.id,
            description: reservoir.description || undefined,
          };
        }
        case "valve": {
          const sector = property.projects
            .flatMap((project) => project.sectors)
            .find((sector) => sector.id === vdc.sector);
          if (!sector) return null;
          return {
            type: "sector" as const,
            name: sector.name,
            id: sector.id,
            description: sector.description || undefined,
          };
        }
        default:
          return null;
      }
    })
    .filter(Boolean) as DeviceAssociation[];
}

/**
 * Format device associations for display
 * @param associations - Array of device associations
 * @returns Formatted association text or null if no associations
 */
export function formatDeviceAssociations(associations: DeviceAssociation[]): string | null {
  if (associations.length === 0) return null;

  if (associations.length === 1) {
    const assoc = associations[0];
    const typeLabels = {
      project: "Projeto",
      water_pump: "Bomba",
      reservoir: "Reservatório",
      sector: "Setor",
    };
    return `${typeLabels[assoc.type]}: ${assoc.name}`;
  }

  // Group associations by type for multiple associations
  const grouped = associations.reduce(
    (acc, assoc) => {
      if (!acc[assoc.type]) acc[assoc.type] = [];
      acc[assoc.type].push(assoc.name);
      return acc;
    },
    {} as Record<string, string[]>
  );

  const parts: string[] = [];
  const typeLabels = {
    project: "Projetos",
    water_pump: "Bombas",
    reservoir: "Reservatórios",
    sector: "Setores",
  };

  for (const [type, names] of Object.entries(grouped)) {
    const label = typeLabels[type as keyof typeof typeLabels];
    if (names.length === 1) {
      parts.push(`${label.slice(0, -1)}: ${names[0]}`); // Singular form
    } else {
      parts.push(`${label}: ${names.join(", ")}`);
    }
  }

  return parts.join(" • ");
}

/**
 * Get association icon based on association type
 * @param type - The association type
 * @returns Emoji icon for the association type
 */
export function getAssociationIcon(type: DeviceAssociation["type"]): string {
  const icons = {
    project: "🌱",
    water_pump: "💧",
    reservoir: "🏗️",
    sector: "🔧",
  };
  return icons[type];
}

/**
 * Find all associations for a water pump based on its ID
 * @param pumpId - The water pump UUID to find associations for
 * @param property - The property containing all related data
 * @returns Array of associations for the water pump
 */
export function findWaterPumpAssociations(pumpId: string, property: AUTProperty | null): DeviceAssociation[] {
  if (!property) return [];

  const associations: DeviceAssociation[] = [];

  // Find projects where this water pump is used for irrigation
  const irrigationProjects = property.projects.filter((project) => project.irrigation_water_pump === pumpId);
  associations.push(
    ...irrigationProjects.map((project) => ({
      type: "project" as const,
      name: `${project.name} (Irrigação)`,
      id: project.id,
      description: project.description || undefined,
    }))
  );

  // Find projects where this water pump is used for fertigation
  const fertigationProjects = property.projects.filter((project) => project.fertigation_water_pump === pumpId);
  associations.push(
    ...fertigationProjects.map((project) => ({
      type: "project" as const,
      name: `${project.name} (Fertirrigação)`,
      id: project.id,
      description: project.description || undefined,
    }))
  );

  // Find reservoirs where this water pump is connected
  const connectedReservoirs = property.reservoirs.filter((reservoir) => reservoir.water_pump === pumpId);
  associations.push(
    ...connectedReservoirs.map((reservoir) => ({
      type: "reservoir" as const,
      name: reservoir.name,
      id: reservoir.id,
      description: reservoir.description || undefined,
    }))
  );

  return associations;
}

/**
 * Check if a device has any associations
 * @param deviceId - The device UUID
 * @param deviceModel - The device model type
 * @param property - The property containing all related data
 * @returns True if the device has associations, false otherwise
 */
export function hasDeviceAssociations(
  deviceId: string,
  deviceModel: DeviceModelId,
  property: AUTProperty | null
): boolean {
  const associations = findDeviceAssociations(deviceId, deviceModel, property);
  return associations.length > 0;
}

/**
 * Find sector-project associations for a valve controller
 * @param deviceId - The valve controller UUID to find associations for
 * @param property - The property containing all related data
 * @returns Array of sector-project associations
 */
export function findValveControllerSectorProjectAssociations(
  deviceId: string,
  property: AUTProperty | null
): SectorProjectAssociation[] {
  if (!property) return [];

  const associations: SectorProjectAssociation[] = [];
  const virtualDeviceConfigurations = property.devices
    .flatMap((pd) => pd.device.virtual_device_configurations)
    .filter((vdc) => vdc.device === deviceId && vdc.equipment_type === "valve");

  property.projects.forEach((project) => {
    if (project.sectors) {
      const sectors = project.sectors.filter((sector) =>
        virtualDeviceConfigurations.some((vdc) => vdc.sector === sector.id)
      );

      associations.push(
        ...sectors.map((sector) => ({
          sectorName: sector.name,
          sectorId: sector.id,
          projectName: project.name,
          projectId: project.id,
        }))
      );
    }
  });

  return associations;
}

/**
 * Format valve controller sector-project associations for display in Portuguese
 * @param associations - Array of sector-project associations
 * @returns Formatted text or null if no associations
 */
export function formatValveControllerAssociations(associations: SectorProjectAssociation[]): string | null {
  if (associations.length === 0) return null;

  // Group sectors by project
  const grouped = associations.reduce(
    (acc, assoc) => {
      if (!acc[assoc.projectId]) {
        acc[assoc.projectId] = {
          projectName: assoc.projectName,
          sectors: [],
        };
      }
      acc[assoc.projectId].sectors.push(assoc.sectorName);
      return acc;
    },
    {} as Record<string, { projectName: string; sectors: string[] }>
  );

  const parts: string[] = [];

  for (const projectGroup of Object.values(grouped)) {
    const sectors = projectGroup.sectors;
    let sectorText: string;

    if (sectors.length === 1) {
      sectorText = sectors[0];
    } else if (sectors.length === 2) {
      sectorText = `${sectors[0]} e ${sectors[1]}`;
    } else {
      const lastSector = sectors[sectors.length - 1];
      const otherSectors = sectors.slice(0, -1).join(", ");
      sectorText = `${otherSectors} e ${lastSector}`;
    }

    parts.push(`${sectorText} em ${projectGroup.projectName}`);
  }

  if (parts.length === 1) {
    return parts[0];
  } else if (parts.length === 2) {
    return `${parts[0]} e ${parts[1]}`;
  } else {
    const lastPart = parts[parts.length - 1];
    const otherParts = parts.slice(0, -1).join(", ");
    return `${otherParts} e ${lastPart}`;
  }
}

/**
 * Check if a water pump has any associations
 * @param pumpId - The water pump UUID
 * @param property - The property containing all related data
 * @returns True if the water pump has associations, false otherwise
 */
export function hasWaterPumpAssociations(pumpId: string, property: AUTProperty | null): boolean {
  const associations = findWaterPumpAssociations(pumpId, property);
  return associations.length > 0;
}
